/* eslint-disable import/prefer-default-export */
import { Stroke, Fill } from 'ol/style';

import { hexToRGB } from './styleUtils';

function isDynamicValue(value) {
  return (
    value &&
    typeof value === 'object' &&
    (value.prop || value.type === 'propertyname' || value.type === 'expression')
  );
}

function resolveStaticColor(value, fallback) {
  if (value == null || value === '' || isDynamicValue(value)) {
    return fallback;
  }
  return value;
}

function resolveStaticNumber(value, fallback) {
  if (value == null || value === '' || isDynamicValue(value)) {
    return fallback;
  }
  const numericValue = Number(value);
  return Number.isNaN(numericValue) ? fallback : numericValue;
}

/**
 * Get an OL style/Stroke instance from the css/svg properties of the .stroke property
 * of an SLD symbolizer object.
 * @private
 * @param  {object} stroke SLD symbolizer.stroke object.
 * @return {object} OpenLayers style/Stroke instance. Returns undefined when input is null or undefined.
 */
export function getSimpleStroke(stroke) {
  // According to SLD spec, if no Stroke element is present inside a symbolizer element,
  // no stroke is to be rendered.
  if (!stroke) {
    return undefined;
  }

  const styleParams = stroke.styling || {};

  let strokeDasharray = null;

  if (styleParams.strokeDasharray && !isDynamicValue(styleParams.strokeDasharray)) {
    strokeDasharray = styleParams.strokeDasharray;
  }

  let strokeDashoffset = null;

  if (styleParams.strokeDashoffset != null && !isDynamicValue(styleParams.strokeDashoffset)) {
    strokeDashoffset = parseFloat(styleParams.strokeDashoffset);
  }

  const strokeColor = resolveStaticColor(styleParams.stroke, 'black');
  const strokeOpacity = resolveStaticNumber(styleParams.strokeOpacity, 1);
  const strokeWidth = resolveStaticNumber(styleParams.strokeWidth, 1);
  const lineDash = Array.isArray(strokeDasharray)
    ? strokeDasharray
    : typeof strokeDasharray === 'string'
      ? strokeDasharray.split(' ')
      : undefined;

  return new Stroke({
    color:
      strokeColor &&
      strokeColor.slice &&
      strokeColor.slice(0, 1) === '#'
        ? hexToRGB(strokeColor, strokeOpacity)
        : strokeColor,
    width: strokeWidth,
    lineCap: styleParams.strokeLinecap,
    lineDash,
    lineDashOffset: strokeDashoffset,
    lineJoin: styleParams.strokeLinejoin,
  });
}

/**
 * Get an OL style/Fill instance from the css/svg properties of the .fill property
 * of an SLD symbolizer object.
 * @private
 * @param  {object} fill SLD symbolizer.fill object.
 * @return {object} OpenLayers style/Fill instance. Returns undefined when input is null or undefined.
 */
export function getSimpleFill(fill) {
  // According to SLD spec, if no Fill element is present inside a symbolizer element,
  // no fill is to be rendered.
  if (!fill) {
    return undefined;
  }

  const styleParams = fill.styling || {};
  const fillColor = resolveStaticColor(styleParams.fill, 'black');
  const fillOpacity = resolveStaticNumber(styleParams.fillOpacity, 1);

  return new Fill({
    color:
      fillColor &&
      fillColor.slice &&
      fillColor.slice(0, 1) === '#'
        ? hexToRGB(fillColor, fillOpacity)
        : fillColor,
  });
}
